/**
 * Registry de temas del módulo de retos.
 *
 * Temas: dark (oscuro) | light (claro)
 *
 * layout → fondo de página (CSS var --layout-main-bg) + showStars
 * card   → tokens CSS (--cdi-*) para PlayerCardBase / ChallengeIntroBody
 *
 * DOM: .challenge-card-base → __hero (__bg-img*, __bg-blur, __tema-card) + __body (slot)
 */

/** Barra de escala */
const CHALLENGE_SCALE_BAR = {
  qsTrackBg: 'rgba(41, 160, 207, 0.4)',
  qsTrackBgAnswered: '#29A0CF',
  qsPointColor: 'rgba(255, 255, 255, 0.45)',
  qsPointColorAnswered: 'rgba(255, 255, 255, 0.45)',
  qsIndicatorBg: 'rgba(171, 223, 244, 0.3)',
  qsIndicatorColor: '#36465E', 
  qsIndicatorBorder: 'rgba(54, 70, 94, 0.9)',
}

export const CHALLENGE_THEMES = {
  dark: {
    layout: {
      background: '#10151C',
      showStars: true,
    },
    card: {
      // Fade oscuro
      bodyBg: '#10151C',
      bgImgFilter: 'none',
      bgImgMask: 'linear-gradient(to bottom, black 0%, black 35%, transparent 95%)',
      bgImgGradient: 'none',
      // Blur inactivo en este tema.
      blurLayerBg: 'none',
      blurLayerFilter: 'blur(0.5px)',
      blurLayerBlend: 'normal',
      titleColor: '#FFFFFF',
      subtitleColor: '#C1C6CD',
      themeColor: '#EBEDEF',
      descriptionColor: '#DDE3EA',
      // Preguntas — fondo por tipo ($blue-light-100)
      qoBg: '#CBD4DE', // options, multiple, yes_no
      qsBg: '#CBD4DE', // scale, bar_scale
      qtBg: '#CBD4DE', // time_picker
      ...CHALLENGE_SCALE_BAR,
      // Favorito 
      iconBg: 'transparent',
      iconBgHover: 'transparent',
      iconColor: '#36465E',
      iconColorHover: '#36465E',
      iconBgActive: 'rgba(142, 161, 187, 0.3)',
      iconColorActive: '#36465E',
      iconBorderGradient:
        'linear-gradient(145deg, rgba(232, 237, 244, 0.55) 6%, rgba(103, 122, 146, 0.4) 92%)',
      iconBorderGradientHover:
        'linear-gradient(145deg, rgba(242, 246, 252, 0.7) 6%, rgba(109, 129, 154, 0.55) 92%)',
      iconBorderGradientActive:
        'linear-gradient(145deg, rgba(208, 219, 233, 0.7) 6%, rgba(92, 111, 135, 0.7) 92%)',
      iconScale: '1',
      // Favorito hero (contorno inactivo)
      favoriteIconColor: '#FFFFFF',
      // Back button
      backIconBg: 'transparent',
      backIconBgHover: 'transparent',
      backIconBgActive: 'rgba(120, 143, 169, 0.4)',
      backIconColor: '#D8DFE9',
      backIconColorHover: '#D8DFE9',
      backIconColorActive: '#36465E',
      backIconBorderGradient:
        'linear-gradient(135deg, rgba(244, 248, 254, 0.95) 0%, rgba(198, 209, 224, 0.45) 52%, rgba(110, 131, 159, 0.65) 100%)',
      backIconBorderGradientHover:
        'linear-gradient(135deg, rgba(251, 253, 255, 0.95) 0%, rgba(212, 221, 234, 0.5) 50%, rgba(113, 136, 164, 0.7) 100%)',
      backIconBorderGradientActive:
        'linear-gradient(135deg, rgba(224, 233, 245, 0.85) 0%, rgba(149, 169, 194, 0.55) 55%, rgba(75, 97, 123, 0.8) 100%)',
      backIconOverlay: 'rgba(54, 92, 143, 0.3)',
      backIconOverlayBlend: 'screen',
      backIconOverlayHover: 'rgba(54, 92, 143, 0.58)',
      backIconOverlayBlendHover: 'multiply',
      // Audio player
      playerBg: 'transparent',
      playerColor: '#ffffff',
      playerTrackBg: '#202a38',
      playerTrackFill: 'rgba(255, 255, 255, 0.45)',
      ambientPlayerBg: 'rgba(32, 42, 56, 0.5)',
      ambientSleepTimerBg: 'rgba(32, 42, 56, 0.9)',
      ambientSleepTimerOptionColor: '#9AABBF',
      ambientSleepTimerSelectedColor: '#FFFFFF',
      // Ghost button — fondo oscuro: se invierten hover/active para mantener contraste
      ghostBtnColor:         '#EBEDEF',
      ghostBtnInteractColor: '#EBEDEF',
      ghostBtnHoverBg:       'rgba(235, 237, 239, 0.08)',
      ghostBtnActiveBg:      'rgba(235, 237, 239, 0.15)',
      // ChallengeNextCard
      nextCardBg:       '#202a38',
      nextCardLabelColor:   '#c8d0db',
      nextCardTitleColor:   '#ebedef',
      nextCardDividerColor: 'rgba(200, 208, 219, 0.2)',
      // ChallengeOverviewBody / ChallengeAudioListBody
      chobDayCardBg:        '#202a38',
      chobDescriptionColor:     '#DDE3EA', // blue-100
      chobEpisodeTitleColor:    '#FFFFFF', // blue-0
      chobEpisodeSubtitleColor: '#9AABBF', // blue-300 (dark)
      chobPlayBtnBg:        '#36465e',
      chobLockColor:        '#9AABBF', // candado (dark)
      chobAudioSelectedBg:  '#36465e',
      chobAudioSelectedBorder: '#2B384B',
    },
  },
  light: {
    layout: {
      background: 'radial-gradient(65.06% 37.99% at 62.01% 100%, #FEDD87 21.15%, #FFF8E5 100%)',
      showStars: false,
    },
    card: {
      // Fade cálido
      bodyBg: 'transparent',
      bgImgFilter: 'none',
      bgImgMask: 'linear-gradient(to bottom, black 0%, black 35%, transparent 95%)',
      bgImgGradient: 'linear-gradient(to bottom, rgba(255, 248, 229, 0) 41.42%, #FFF8E5 100%)',
      // Frost sutil
      blurLayerBg: 'none',
      blurLayerFilter: 'blur(0.6px)',
      blurLayerBlend: 'lighten',
      titleColor: '#36465E',
      subtitleColor: '#667786',
      themeColor: '#36465E',
      descriptionColor: '#56657A',
      // Preguntas — fondo por tipo ($yellow-custom-white-600)
      qoBg: '#F7F2E8', // options, multiple, yes_no
      qsBg: '#F7F2E8', // scale, bar_scale
      qtBg: '#F7F2E8', // time_picker
      ...CHALLENGE_SCALE_BAR,
      // Favorito
      iconBg: 'rgba(200, 208, 219, 0.2)',
      iconBgHover: 'rgba(200, 208, 219, 0.2)',
      iconColor: '#D8DFE9',
      iconColorHover: '#D8DFE9',
      iconBgActive: 'rgba(120, 143, 169, 0.3)',
      iconColorActive: '#36465E',
      iconBorderGradient: 'transparent',
      iconBorderGradientHover:
        'linear-gradient(145deg, rgba(248, 251, 255, 0.8) 6%, rgba(137, 157, 182, 0.62) 92%)',
      iconBorderGradientActive:
        'linear-gradient(145deg, rgba(221, 230, 241, 0.75) 6%, rgba(89, 109, 133, 0.72) 92%)',
      iconScale: '1',
      // Favorito hero (contorno inactivo)
      favoriteIconColor: '#000000',
      // Back button
      backIconBg: 'rgba(200, 208, 219, 0.4)',
      backIconBgHover: 'rgba(200, 208, 219, 0.4)',
      backIconBgActive: 'rgba(142, 161, 187, 0.4)',
      backIconColor: '#36465E',
      backIconColorHover: '#36465E',
      backIconColorActive: '#36465E',
      backIconBorderGradient:
        'linear-gradient(135deg, rgba(226, 234, 243, 0.68) 0%, rgba(138, 157, 181, 0.25) 50%, rgba(93, 112, 137, 0.5) 100%)',
      backIconBorderGradientHover:
        'linear-gradient(135deg, rgba(228, 235, 244, 0.72) 0%, rgba(139, 158, 182, 0.3) 50%, rgba(97, 116, 141, 0.55) 100%)',
      backIconBorderGradientActive:
        'linear-gradient(135deg, rgba(220, 230, 242, 0.82) 0%, rgba(149, 169, 195, 0.45) 50%, rgba(83, 103, 128, 0.72) 100%)',
      // Audio player
      playerBg: 'transparent',
      playerColor: '#222e3b',
      playerTrackBg: 'rgba(54, 70, 94, 0.15)',
      playerTrackFill: '#36465e',
      ambientPlayerBg: 'rgba(255, 251, 242, 0.5)',
      ambientSleepTimerBg: 'rgba(255, 251, 242, 0.9)',
      ambientSleepTimerOptionColor: '#667786',
      ambientSleepTimerSelectedColor: '#222e3b',
      // Ghost button — fondo claro: usa los defaults del DS (overlay oscuro sobre fondo amarillo)
      ghostBtnColor:         '#2b384b',
      ghostBtnInteractColor: '#222e3b',
      ghostBtnHoverBg:       'rgba(34, 46, 59, 0.08)',
      ghostBtnActiveBg:      'rgba(34, 46, 59, 0.15)',
      // ChallengeNextCard
      nextCardBg:       '#fffdf7',
      nextCardLabelColor:   '#778396',
      nextCardTitleColor:   '#36465e',
      nextCardDividerColor: 'rgba(54, 70, 94, 0.15)',
      // ChallengeOverviewBody / ChallengeAudioListBody
      chobDayCardBg:        '#fffdf7',
      chobDescriptionColor:     '#56657A', // navy-400
      chobEpisodeTitleColor:    '#36465E', // $blue-500
      chobEpisodeSubtitleColor: '#788393', // $blue-300
      chobPlayBtnBg:        '#56657a',
      chobLockColor:        '#778396', // navy-300
      chobAudioSelectedBg:  '#36465e',
      chobAudioSelectedBorder: '#2B384B',
    },
  },
}

export const DEFAULT_CHALLENGE_THEME = 'dark'

export const CHALLENGE_THEME_KEYS = Object.keys(CHALLENGE_THEMES)

/**
 * @param {string | undefined | null} key
 * @returns {string}
 */
export function resolveChallengeTheme(key) {
  return CHALLENGE_THEMES[key] ? key : DEFAULT_CHALLENGE_THEME
}

// Fallbacks de iconos en getCardThemeVars.
const ICON_DEFAULTS = {
  bg: 'transparent',
  color: '#36465E',
  borderGradient:
    'linear-gradient(145deg, rgba(255,255,255,0.5), rgba(122,140,165,0.35))',
}

/** Mapea un tema a CSS vars (--cdi-*) para PlayerCardBase. */
export function getCardThemeVars(key) {
  const themeKey = resolveChallengeTheme(key)
  const { card } = CHALLENGE_THEMES[themeKey]

  return {
    '--cdi-body-bg':            card.bodyBg,
    '--cdi-bg-img-filter':      card.bgImgFilter ?? 'none',
    '--cdi-bg-img-mask':        card.bgImgMask ?? 'none',
    '--cdi-bg-img-gradient':    card.bgImgGradient ?? 'none',
    '--cdi-blur-layer-bg':      card.blurLayerBg ?? 'none',
    '--cdi-blur-layer-filter':  card.blurLayerFilter ?? 'none',
    '--cdi-blur-layer-blend':   card.blurLayerBlend ?? 'normal',
    '--cdi-title-color':        card.titleColor,
    '--cdi-subtitle-color':     card.subtitleColor,
    '--cdi-theme-color':        card.themeColor,
    '--cdi-description-color':  card.descriptionColor,
    '--cdi-icon-bg':            card.iconBg            ?? ICON_DEFAULTS.bg,
    '--cdi-icon-bg-hover':      card.iconBgHover       ?? card.iconBg ?? ICON_DEFAULTS.bg,
    '--cdi-icon-bg-active':     card.iconBgActive      ?? card.iconBg ?? ICON_DEFAULTS.bg,
    '--cdi-icon-color':         card.iconColor         ?? ICON_DEFAULTS.color,
    '--cdi-icon-color-hover':   card.iconColorHover    ?? card.iconColor ?? ICON_DEFAULTS.color,
    '--cdi-icon-color-active':  card.iconColorActive   ?? card.iconColor ?? ICON_DEFAULTS.color,
    '--cdi-icon-border-gradient':
      card.iconBorderGradient        ?? ICON_DEFAULTS.borderGradient,
    '--cdi-icon-border-gradient-hover':
      card.iconBorderGradientHover   ?? card.iconBorderGradient ?? ICON_DEFAULTS.borderGradient,
    '--cdi-icon-border-gradient-active':
      card.iconBorderGradientActive  ?? card.iconBorderGradient ?? ICON_DEFAULTS.borderGradient,
    '--cdi-icon-scale':         card.iconScale ?? '1',
    '--cdi-favorite-icon-color': card.favoriteIconColor ?? ICON_DEFAULTS.color,
    '--cdi-player-bg':          card.playerBg         ?? 'transparent',
    '--cdi-player-color':       card.playerColor      ?? '#222e3b',
    '--cdi-player-track-bg':    card.playerTrackBg    ?? '#202a38',
    '--cdi-player-track-fill':  card.playerTrackFill  ?? '#36465e',
    '--cdi-ambient-player-bg':       card.ambientPlayerBg       ?? 'transparent',
    '--cdi-ambient-sleep-timer-bg':             card.ambientSleepTimerBg             ?? card.ambientPlayerBg ?? 'transparent',
    '--cdi-ambient-sleep-timer-option-color':   card.ambientSleepTimerOptionColor      ?? card.subtitleColor ?? '#667786',
    '--cdi-ambient-sleep-timer-selected-color': card.ambientSleepTimerSelectedColor    ?? card.themeColor ?? card.playerColor ?? '#36465E',
    // Ghost button
    '--ghost-btn-color':          card.ghostBtnColor         ?? '#567394',
    '--ghost-btn-interact-color': card.ghostBtnInteractColor ?? '#222e3b',
    '--ghost-btn-hover-bg':       card.ghostBtnHoverBg       ?? 'rgba(34, 46, 59, 0.08)',
    '--ghost-btn-active-bg':      card.ghostBtnActiveBg      ?? 'rgba(34, 46, 59, 0.15)',
    // ChallengeNextCard
    '--cnc-bg':            card.nextCardBg           ?? '#202a38',
    '--cnc-label-color':   card.nextCardLabelColor   ?? '#c8d0db',
    '--cnc-title-color':   card.nextCardTitleColor   ?? '#ebedef',
    '--cnc-divider-color': card.nextCardDividerColor ?? 'rgba(200, 208, 219, 0.2)',
    // QuestionnaireScale
    '--qs-bg':                     card.qsBg,
    '--qs-counter-color':          card.qsCounterColor,
    '--qs-question-color':         card.qsQuestionColor,
    '--qs-question-emphasis-color': card.qsQuestionEmphasisColor,
    '--qs-description-color':      card.qsDescriptionColor,
    '--qs-sub-description-color':  card.qsSubDescriptionColor,
    '--qs-wrapper-bg':             card.qsWrapperBg,
    '--qs-label-bg':               card.qsLabelBg,
    '--qs-label-color':            card.qsLabelColor,
    '--qs-number-color':           card.qsNumberColor,
    '--qs-number-color-hover':     card.qsNumberColorHover,
    '--qs-track-bg':               card.qsTrackBg,
    '--qs-track-bg-answered':      card.qsTrackBgAnswered,
    '--qs-point-color':            card.qsPointColor,
    '--qs-point-color-answered':   card.qsPointColorAnswered,
    '--qs-indicator-border':       card.qsIndicatorBorder,
    '--qs-indicator-bg':           card.qsIndicatorBg,
    '--qs-indicator-color':        card.qsIndicatorColor,
    '--qs-indicator-gradient':     card.qsIndicatorGradient,
    '--qs-indicator-line-color':   card.qsIndicatorLineColor,
    // QuestionnaireOptions — emite solo los tokens definidos. Si el tema no los
    // declara, NO se inyecta la var → el componente cae a su fallback (paleta
    // original de question-types). Así los consumers no-challenge no se afectan.
    '--qo-bg':                       card.qoBg,
    '--qo-counter-color':            card.qoCounterColor,
    '--qo-question-color':           card.qoQuestionColor,
    '--qo-question-emphasis-color':  card.qoQuestionEmphasisColor,
    '--qo-description-color':        card.qoDescriptionColor,
    '--qo-sub-description-color':    card.qoSubDescriptionColor,
    '--qo-badge-bg':                 card.qoBadgeBg,
    '--qo-badge-color':              card.qoBadgeColor,
    '--qo-pill-bg':                  card.qoPillBg,
    '--qo-pill-color':               card.qoPillColor,
    '--qo-pill-border':              card.qoPillBorder,
    '--qo-pill-bg-selected':         card.qoPillBgSelected,
    '--qo-pill-color-selected':      card.qoPillColorSelected,
    '--qo-pill-border-selected':     card.qoPillBorderSelected,
    '--qo-input-color':              card.qoInputColor,
    '--qo-input-border':             card.qoInputBorder,
    '--qo-input-border-focus':       card.qoInputBorderFocus,
    '--qo-input-placeholder':        card.qoInputPlaceholder,
    '--qt-bg':                       card.qtBg,
    // ChallengeOverviewBody / ChallengeAudioListBody
    '--chob-day-card-bg':             card.chobDayCardBg          ?? '#202a38',
    '--chob-description-color':       card.chobDescriptionColor   ?? '#DDE3EA',
    '--chob-episode-title-color':     card.chobEpisodeTitleColor  ?? '#36465e',
    '--chob-episode-subtitle-color':  card.chobEpisodeSubtitleColor ?? '#788393',
    '--chob-play-btn-bg':             card.chobPlayBtnBg          ?? '#36465e',
    '--chob-lock-color':              card.chobLockColor          ?? '#778396',
    '--chob-audio-selected-bg':       card.chobAudioSelectedBg    ?? '#36465e',
    '--chob-audio-selected-border':   card.chobAudioSelectedBorder ?? '#2b384b',
  }
}

/** Fondo de página para --layout-main-bg (PrincipalLayout). */
export function getLayoutBackground(key) {
  const themeKey = resolveChallengeTheme(key)
  return CHALLENGE_THEMES[themeKey].layout.background
}

/** Si ChallengesView muestra PlayerBgStars. */
export function getShowStars(key) {
  const themeKey = resolveChallengeTheme(key)
  return CHALLENGE_THEMES[themeKey].layout.showStars
}

/** Override de icon vars para el botón back  */
export function getBackIconOverrideVars(key) {
  const themeKey = resolveChallengeTheme(key)
  const { card } = CHALLENGE_THEMES[themeKey]

  return {
    '--cdi-icon-bg':                     card.backIconBg,
    '--cdi-icon-bg-hover':               card.backIconBgHover,
    '--cdi-icon-bg-active':              card.backIconBgActive,
    '--cdi-icon-color':                  card.backIconColor,
    '--cdi-icon-color-hover':            card.backIconColorHover,
    '--cdi-icon-color-active':           card.backIconColorActive,
    '--cdi-icon-border-gradient':        card.backIconBorderGradient,
    '--cdi-icon-border-gradient-hover':  card.backIconBorderGradientHover,
    '--cdi-icon-border-gradient-active': card.backIconBorderGradientActive,
    '--cdi-icon-border-size-hover':      card.backIconBorderSizeHover ?? '1px',
    '--cdi-icon-overlay':                card.backIconOverlay,
    '--cdi-icon-overlay-blend':          card.backIconOverlayBlend,
    '--cdi-icon-overlay-hover':          card.backIconOverlayHover,
    '--cdi-icon-overlay-blend-hover':    card.backIconOverlayBlendHover,
    '--cdi-icon-scale-hover':            '1',
    '--cdi-icon-scale-active':           '1',
  }
}
